/**
 * Errores de login de los portales CLIENTE y PARTNER (contrato F1).
 *
 * El backend transporta el CÓDIGO funcional en la cabecera `X-Auth-Error-Code`
 * (INVALID_CREDENTIALS / ACCOUNT_LOCKED / INACTIVE_USER) y los dos datos que
 * hacen el mensaje útil en cabeceras propias: `X-Auth-Remaining-Attempts` y
 * `X-Auth-Locked-Minutes`. Traducirlos vive AQUÍ y no en cada página porque los
 * dos portales tienen que decir exactamente lo mismo — es el mismo contrato.
 *
 * Aquí vive también el otro extremo del contrato: el 403
 * PASSWORD_CHANGE_REQUIRED que las dependencias de auth devuelven a una sesión
 * YA abierta que arrastra la contraseña de un solo uso (veto en servidor).
 *
 * Réplica del `authErrorMessage` del frontend de plataforma
 * (frontend-react/src/services/authService.ts).
 */
import i18next from 'i18next';

/** Código del veto en servidor (403 con sesión válida, NO sesión caducada). */
export const PASSWORD_CHANGE_REQUIRED_CODE = 'PASSWORD_CHANGE_REQUIRED';

/**
 * Evento con el que los clientes HTTP avisan a su contexto de auth de que el
 * backend está vetando por contraseña pendiente. Se usa un evento de `window`
 * —y no un import directo del contexto— porque el cliente HTTP es un singleton
 * creado fuera de React: es la única forma de empujar estado hacia arriba sin
 * un ciclo de imports ni una recarga de página (que perdería el error).
 */
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'wai:portal-password-change-required';

/** Lee `X-Auth-Error-Code` tanto de un `Headers` de fetch como del objeto de axios. */
const errorCodeFromHeaders = (headers: any): string | undefined => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get('X-Auth-Error-Code') || undefined;
  // Axios normaliza los nombres a minúsculas.
  return headers['x-auth-error-code'] || headers['X-Auth-Error-Code'] || undefined;
};

/**
 * ¿Esta respuesta es el veto por contraseña pendiente? Se exige el 403 Y el
 * código: un 403 a secas es «no tienes permiso» (p.ej. la sesión de
 * impersonación en solo lectura) y no debe llevar a nadie al gate.
 */
export function isPasswordChangeRequiredResponse(response: any): boolean {
  if (!response || response.status !== 403) return false;
  return errorCodeFromHeaders(response.headers) === PASSWORD_CHANGE_REQUIRED_CODE;
}

/** Avisa al contexto de auth del portal para que levante el gate bloqueante. */
export function notifyPasswordChangeRequired(portal: 'customer' | 'partner'): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(PASSWORD_CHANGE_REQUIRED_EVENT, { detail: { portal } })
  );
}

/** Suscripción al evento; devuelve la función de baja (para el cleanup del efecto). */
export function onPasswordChangeRequired(
  portal: 'customer' | 'partner',
  handler: () => void
): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ portal?: string }>).detail;
    if (!detail?.portal || detail.portal === portal) handler();
  };
  window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, listener);
  return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, listener);
}

export class PortalLoginError extends Error {
  code?: string;
  status?: number;
  remainingAttempts?: number;
  lockedMinutes?: number;

  constructor(
    message: string,
    opts: { code?: string; status?: number; remainingAttempts?: number; lockedMinutes?: number } = {}
  ) {
    super(message);
    this.name = 'PortalLoginError';
    this.code = opts.code;
    this.status = opts.status;
    this.remainingAttempts = opts.remainingAttempts;
    this.lockedMinutes = opts.lockedMinutes;
  }
}

const num = (raw: string | null | undefined): number | undefined => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** Mensaje legible a partir del código + status + datos de la cabecera. */
export function portalLoginErrorMessage(
  code: string | null | undefined,
  status: number | undefined,
  extra: { remainingAttempts?: number; lockedMinutes?: number } = {}
): string {
  const { remainingAttempts, lockedMinutes } = extra;

  switch (code) {
    case 'INVALID_CREDENTIALS': {
      const base = i18next.t('portalAuth.invalidCredentials', 'Email o contraseña incorrectos');
      if (typeof remainingAttempts === 'number' && remainingAttempts > 0) {
        const queda = remainingAttempts === 1 ? 'queda' : 'quedan';
        const intento = remainingAttempts === 1 ? 'intento' : 'intentos';
        return `${base}. ${i18next.t('portalAuth.attemptsLeft', {
          count: remainingAttempts,
          defaultValue: `Te ${queda} ${remainingAttempts} ${intento} antes de que se bloquee la cuenta.`,
        })}`;
      }
      return base;
    }
    case 'ACCOUNT_LOCKED': {
      if (typeof lockedMinutes === 'number' && lockedMinutes > 0) {
        return i18next.t('portalAuth.accountLockedMinutes', {
          count: lockedMinutes,
          defaultValue:
            `Cuenta bloqueada por demasiados intentos fallidos. Inténtalo de nuevo en ` +
            `${lockedMinutes} minuto${lockedMinutes === 1 ? '' : 's'} o pide a un administrador que la desbloquee.`,
        });
      }
      return i18next.t(
        'portalAuth.accountLocked',
        'Cuenta bloqueada por demasiados intentos fallidos. Inténtalo más tarde o pide a un administrador que la desbloquee.'
      );
    }
    case 'INACTIVE_USER':
      return i18next.t('portalAuth.inactiveUser', 'Esta cuenta está desactivada. Contacta con WAI.');
    case PASSWORD_CHANGE_REQUIRED_CODE:
      // No sale del login (el login responde 200 con el flag), pero si un
      // despliegue a medias lo devolviera aquí, el texto debe llevar al gate y
      // no parecer un rechazo de credenciales.
      return i18next.t(
        'portalAuth.passwordChangeRequired',
        'Entraste con una contraseña temporal: crea tu contraseña definitiva para continuar.'
      );
    case 'RESELLER_SUSPENDED':
      return i18next.t(
        'portalAuth.resellerSuspended',
        'Tu cuenta de partner está suspendida; contacta con el marketplace.'
      );
    // Solo-Google intentando entrar con contraseña. El mensaje DICE cuál es su
    // puerta: si solo dijera «no permitido», seguiría probando contraseñas.
    case 'METHOD_NOT_ALLOWED':
      return i18next.t(
        'portalAuth.methodNotAllowed',
        'Este usuario inicia sesión con Google. Usa «Entrar con Google».'
      );
    // Llega por Google alguien que no está habilitado para Google (o no existe).
    // Un solo mensaje para los dos casos: distinguirlos delataría qué
    // direcciones tienen cuenta en el marketplace.
    case 'GOOGLE_LOGIN_FAILED':
      return i18next.t(
        'portalAuth.googleFailed',
        'No hemos podido entrar con esa cuenta de Google. Comprueba que es la cuenta con la que te dieron de alta, o entra con tu contraseña.'
      );
    // No debería llegar a la pantalla (el flujo lo sondea), pero si un
    // despliegue a medias lo sacara, el texto no puede parecer un rechazo.
    case 'GOOGLE_LOGIN_PENDING':
      return i18next.t(
        'portalAuth.googlePending',
        'El acceso con Google aún no ha terminado. Completa el consentimiento en la ventana de Google.'
      );
    default:
      break;
  }

  // Sin código conocido: mensaje por status (backend antiguo o error de red).
  if (status === 423) {
    return i18next.t(
      'portalAuth.accountLocked',
      'Cuenta bloqueada por demasiados intentos fallidos. Inténtalo más tarde o pide a un administrador que la desbloquee.'
    );
  }
  if (status === 403) {
    return i18next.t('portalAuth.forbidden', 'No tienes permiso para entrar.');
  }
  if (status === 401) {
    return i18next.t('portalAuth.invalidCredentials', 'Email o contraseña incorrectos');
  }
  if (status && status >= 500) {
    return i18next.t('portalAuth.server', 'Error del servidor. Inténtalo de nuevo más tarde.');
  }
  return i18next.t('portalAuth.general', 'No se pudo iniciar sesión. Inténtalo de nuevo.');
}

/** Construye el error desde una `Response` de fetch (portal cliente). */
export async function portalLoginErrorFromFetch(response: Response): Promise<PortalLoginError> {
  const code = response.headers.get('X-Auth-Error-Code');
  const remainingAttempts = num(response.headers.get('X-Auth-Remaining-Attempts'));
  const lockedMinutes = num(response.headers.get('X-Auth-Locked-Minutes'));
  // El `detail` se ignora a propósito para el texto: el mensaje lo manda el
  // código funcional, que es lo único traducible. Se conserva como respaldo
  // si el backend no envió cabecera (despliegue a medias).
  let detail = '';
  try {
    const body = await response.json();
    detail = typeof body?.detail === 'string' ? body.detail : '';
  } catch {
    /* cuerpo no-JSON: nos quedamos con el mensaje por código */
  }
  const message = code
    ? portalLoginErrorMessage(code, response.status, { remainingAttempts, lockedMinutes })
    : detail || portalLoginErrorMessage(null, response.status);
  return new PortalLoginError(message, {
    code: code || undefined,
    status: response.status,
    remainingAttempts,
    lockedMinutes,
  });
}

/** Construye el error desde un AxiosError sin normalizar (portal partner). */
export function portalLoginErrorFromAxios(error: any): PortalLoginError {
  const response = error?.response;
  if (!response) {
    return new PortalLoginError(
      i18next.t('portalAuth.connection', 'Error de conexión. Revisa tu conexión a internet.')
    );
  }
  // Axios normaliza los nombres de cabecera a minúsculas.
  const headers = response.headers || {};
  const code: string | undefined = headers['x-auth-error-code'] || undefined;
  const remainingAttempts = num(headers['x-auth-remaining-attempts']);
  const lockedMinutes = num(headers['x-auth-locked-minutes']);
  const detail = typeof response.data?.detail === 'string' ? response.data.detail : '';
  const message = code
    ? portalLoginErrorMessage(code, response.status, { remainingAttempts, lockedMinutes })
    : detail || portalLoginErrorMessage(null, response.status);
  return new PortalLoginError(message, {
    code,
    status: response.status,
    remainingAttempts,
    lockedMinutes,
  });
}
